// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Host, Element, Prop, State, Watch, h, Build, Env } from '@stencil/core';

import { DeviceType } from '../../../../types/base/affiliate/characteristics';
import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { FormattedPromotion } from '../../types';
import { requiredParamsCheck, formatPromotions } from '../../utils';
import { ageClass } from '../../utils/age-check';
import { getPromotionEvent, debouncedLogEvent } from '../../utils/analytics';
import { fetchPromo, fetchFeaturedPromo, getPlacement } from '../../utils/api';
import { filterPromotions } from '../../utils/filter';
import { fireImpressions } from '../../utils/impressions';
import { registerElToObserverList } from '../../utils/observer';
import PromotionCard from '../functional/PromotionCard';

@Component({
    tag: 'bam-inline-promotion',
    styleUrl: 'bam-inline-promotion.css',
    shadow: true,
})
export class BamInlinePromotion {
    @Element() el: HTMLElement;

    private defaultCallback() {
        return null;
    }
    /** You cannot pass this parameter via an attribute. You must set the callback function after the DOM has loaded. */
    @Prop() callback: () => void = this.defaultCallback;

    // props
    /** (required) */
    @Prop() propertyId: number;
    /** (required) */
    @Prop() context: string;

    /** If you don't provide a promotion id, you must provide a placement id */
    @Prop() promotionId: number;
    @Prop() dynamic: boolean;
    @Prop() affiliateId: number;

    /** If you don't provide a placement id, you must provide a single promotion id */
    @Prop() placementId: number;
    @Prop({ mutable: true }) location: string;

    // optional characteristics props
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;
    @Prop() userParentBookIds: string;
    @Prop() isLoggedIn: boolean;
    @Prop() sponsorSlug: string;

    // optional props for functionality
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() affiliate: string;
    @Prop() affiliateType: string;
    @Prop() internalId: string;
    @Prop() age: number;
    @Prop() hideUnderage = false;
    @Prop() darkMode = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';

    @State() promotion: FormattedPromotion;
    @State() bonusCodeWasClicked = false;
    @State() termsToggle = false;

    onView() {
        if (!Build.isTesting && !Build.isServer) {
            this.logPromotionEvent('Impression');
            fireImpressions(
                this.promotion.impressionUrls,
                this.promotion.affiliateId,
                this.promotion.impressionTtl,
                this.promotion.impressionThrottle,
                this.promotion.impressionThrottleClick
            );
        }
    }

    @Watch('promotion')
    onPromotionChange(newPromotion) {
        if (newPromotion && !Build.isTesting) {
            registerElToObserverList(this.el, this.onView.bind(this));
        }
    }

    async componentWillLoad() {
        if (Env.BAM_HYDRATE !== 'true') {
            this.getPromo();
        }
        if (Env.BAM_HYDRATE === 'true') {
            await this.getPromo();
        }
    }

    async getPromo() {
        if (this.placementId) {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.formatAndSetPromotion.bind(this),
                this.devMode,
                'Inline Promotion'
            );
            return;
        }
        if (this.dynamic) {
            const hasMissingParams = requiredParamsCheck(
                {
                    property_id: this.propertyId,
                    affiliate_id: this.affiliateId,
                },
                'Inline Promotion'
            );

            if (hasMissingParams) {
                return;
            }

            const promotion = await fetchFeaturedPromo(this.propertyId, this.affiliateId, this.context, this.devMode);

            this.promotion = promotion;
        } else {
            const hasMissingParams = requiredParamsCheck(
                {
                    promotion_id: this.promotionId,
                    property_id: this.propertyId,
                },
                'Inline Promotion'
            );

            if (hasMissingParams) {
                return;
            }

            const promotion = await fetchPromo(this.propertyId, this.promotionId, this.context, this.devMode);

            this.promotion = promotion;
        }
    }

    formatAndSetPromotion(placement: AffiliateControllerResponse, location: string) {
        const promos = placement.promotions;
        const promotions = formatPromotions(promos, {
            allowAltLink: placement.allow_alternate_link,
            promotionListId: placement.id,
            location: location,
            context: this.context,
            propertyId: this.propertyId,
        });
        const filteredPromotions = filterPromotions(
            {
                internal_identifier: this.internalId,
                affiliate_name: this.affiliate,
                affiliate_type: this.affiliateType,
            },
            promotions
        );
        this.promotion = filteredPromotions && filteredPromotions.length > 0 && filteredPromotions[0];
        return;
    }

    handleBonusCodeClick() {
        this.bonusCodeWasClicked = true;

        setTimeout(() => {
            this.bonusCodeWasClicked = false;
        }, 2500);
    }

    handleTermsClick() {
        this.termsToggle = !this.termsToggle;
    }

    logPromotionEvent(action: 'Impression' | 'Click') {
        if (action === 'Click') {
            this.callback();
        }

        const i13n = getPromotionEvent({
            componentType: 'Inline Promotion',
            action,
            options: {
                stateCode: null,
                context: this.context,
                propertyId: Number(this.propertyId),
                placementId: null,
                league: null,
                allPromotions: [this.promotion],
                pageSlug: null,
            },
            promotion: this.promotion,
        });
        debouncedLogEvent(this.propertyId, i13n);
    }

    render() {
        return (
            <Host class={ageClass(this.age, [this.promotion], this.hideUnderage)}>
                {this.promotion && (
                    <div class={`${this.darkMode ? 'dark-mode' : ''}`}>
                        <PromotionCard
                            promotion={this.promotion}
                            logPromotionClick={this.logPromotionEvent.bind(this, 'Click')}
                            handleBonusCodeClick={this.handleBonusCodeClick.bind(this)}
                            bonusCodeWasClicked={this.bonusCodeWasClicked}
                            terms={this.terms}
                            termsToggle={this.termsToggle}
                            handleTermsClick={this.handleTermsClick.bind(this)}
                        />
                    </div>
                )}
            </Host>
        );
    }
}
